import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosMultiSelect, type AerosMultiSelectProps } from "../AerosMultiSelect"

interface FormMultiSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
> extends Omit<AerosMultiSelectProps<TValue>, "name" | "defaultValue" | "value" | "onChange"> {
  control: Control<TFieldValues>
  name: TName
  onChange?: (value: TValue[]) => void
}

export const FormMultiSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
>({
  control,
  name,
  helperText,
  onChange,
  ...props
}: FormMultiSelectProps<TFieldValues, TName, TValue>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: TValue[]) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosMultiSelect<TValue>
      {...field}
      {...props}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={handleChange}
    />
  )
}
